import React, { useEffect } from 'react'

// Components
import { useStripe } from 'containers/StripeProvider'
import { useModalContext } from 'containers/ModalManager'
import Button from 'components/Button'
import Section from 'components/Section'

// Local Components
import {
  PlanOverviewPanel,
  BillingHistoryPanel,
  PanelSeparator,
  PaymentInformationPanel,
  WorkspaceSettingsSubscribePanel,
} from 'modules/workspace/components'

// Modals
import {
  CancelScheduleChangeModal,
  CancelWorkspaceSubscriptionModal,
  ChangeNextBillingCycleModal,
  ReactivateSubscriptionModal,
} from 'modules/workspace/modals'

// Types
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  BillingDetailsFragment,
  PaymentDetailsFragment,
} from 'gqlTypes'

// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from 'gqlTypes/expansiveTypes'

// Utils
import {
  isWorkspaceSubscriptionActive,
  isWorkspaceSubscriptionTrialling,
  hasPaymentDetails,
} from 'modules/workspace/utils'

interface BillingProps {
  currentPlan?: BillingPlanFragment
  credits: number
  seats: BillingSeatsInfoFragment
  workspaceId: string
  customerId: string
  subscriptionStatus: BillingStatus
  nextBillingCycleDate?: string
  projectedCost?: number
  subscriptionEnd?: string
  billingDetails: BillingDetailsFragment
  paymentDetails?: PaymentDetailsFragment
  trialEnd: string
  pendingSCAToken?: string
  isWorkspaceSubscribed?: boolean
  isFinanceRole?: boolean
  onWorkspaceSettingRefresh: () => Promise<void>
}

/**
 *
 * Billing
 *
 * Renders the "Billing" page content in "Workspace Settings"
 */
export const WorkspaceSettingsBilling: React.FC<BillingProps> = props => {
  const {
    currentPlan,
    credits,
    seats,
    workspaceId,
    customerId,
    projectedCost,
    subscriptionStatus,
    subscriptionEnd,
    nextBillingCycleDate,
    billingDetails,
    paymentDetails,
    trialEnd,
    pendingSCAToken,
    onWorkspaceSettingRefresh,
    isFinanceRole,
  } = props

  const { load: loadStripe } = useStripe()
  const { showModal } = useModalContext()

  // Load stripe in order previously
  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  const isWorkspaceOnTrial = isWorkspaceSubscriptionTrialling(
    subscriptionStatus
  )
  const isSubscriptionActive = isWorkspaceSubscriptionActive(subscriptionStatus)
  const customerHasPaymentDetails = hasPaymentDetails(paymentDetails)

  const shouldHidePaymentDetails =
    credits !== 0 || (paymentDetails && paymentDetails.type !== 'NONE')

  const canCancelSubscription =
    isSubscriptionActive &&
    !subscriptionEnd &&
    nextBillingCycleDate &&
    customerHasPaymentDetails &&
    !isFinanceRole

  return (
    <>
      {!customerHasPaymentDetails && (
        <PanelSeparator>
          <WorkspaceSettingsSubscribePanel workspaceId={workspaceId} />
        </PanelSeparator>
      )}
      <PanelSeparator>
        <PlanOverviewPanel
          workspaceId={workspaceId}
          currentPlan={currentPlan}
          seats={seats}
          isWorkspaceSubscriptionActive={isSubscriptionActive}
          isWorkspaceOnTrial={isWorkspaceOnTrial}
          customerHasPaymentDetails={customerHasPaymentDetails}
          subscriptionEnd={subscriptionEnd}
          nextBillingCycleDate={nextBillingCycleDate}
          projectedCost={projectedCost}
          trialEndDate={trialEnd}
          onReactivateSubscription={() => {
            showModal(ReactivateSubscriptionModal, {
              currentPlan: currentPlan!,
              subscriptionEnd: subscriptionEnd!,
              customerId,
              workspaceId,
            })
          }}
          onSeatScheduleChange={() => {
            currentPlan &&
              showModal(ChangeNextBillingCycleModal, {
                currentPlan,
                customerId,
                currentSeats: seats.currentSeatsTotal,
                scheduledSeats: seats.scheduledSeatsTotal || undefined,
                workspaceId,
                nextBillingCycleDate,
              })
          }}
          onCancelSeatScheduleChange={() => {
            nextBillingCycleDate &&
              showModal(CancelScheduleChangeModal, {
                workspaceId,
                currentSeats: seats.currentSeatsTotal,
                customerId,
              })
          }}
        />
      </PanelSeparator>
      {shouldHidePaymentDetails && (
        <PanelSeparator>
          <PaymentInformationPanel
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
            credits={credits}
            customerId={customerId}
            billingDetails={billingDetails}
            paymentDetails={paymentDetails}
            subscriptionStatus={subscriptionStatus}
            pendingSCAToken={pendingSCAToken}
          />
        </PanelSeparator>
      )}
      <PanelSeparator>
        <BillingHistoryPanel
          customerId={customerId}
          workspaceId={workspaceId}
          trialEndDate={trialEnd}
          isWorkspaceOnTrial={isWorkspaceOnTrial}
          cancelAtPeriodEnd={subscriptionEnd}
          customerHasPaymentDetails={customerHasPaymentDetails}
        />
      </PanelSeparator>

      {canCancelSubscription && (
        <PanelSeparator>
          {/* "data-testid="danger-zone-panel"" is used to keep consistency with the older panels in tests */}
          <Section
            data-testid="danger-zone-panel"
            title="Cancel Subscription"
            text="After canceling, you'll be able to use Sketch until the end of the current billing period."
            action={
              <Button
                onClick={() =>
                  showModal(CancelWorkspaceSubscriptionModal, {
                    customerId,
                    nextBillingCycleDate: nextBillingCycleDate!,
                    workspaceId,
                  })
                }
                tiny
                variant="negative-secondary">
                Cancel Subscription…
              </Button>
            }
          />
        </PanelSeparator>
      )}
    </>
  )
}
